import Vue from 'vue'
import 'modules/affix/affix'
import state from 'lib/appState'
import Cart from 'lib/cart'
import queryString from 'query-string'
import {
  PURCHASE_TYPE_SUBSCRIPTION,
  PURCHASE_TYPE_ONETIME
} from 'lib/constants'
import 'modules/tooltip/tooltip.vue.js'
import { formatPrice, uniqueNumber } from 'lib/util'
import PcbProducts from 'modules/pcb-products/pcb-products.vue'
import PcbBundleItems from 'modules/pcb-bundle-items/pcb-bundle-items.vue'
import bus from 'lib/bus'
import scrollMonitor from 'scrollmonitor'
import setQuery from 'set-query-string'

export default (el) => new Vue({
  el,
  components: {
    PcbProducts,
    PcbBundleItems
  },
  data () {
    const product = window.HU.product
    const bundleCollectionHandle = window.HU.custom_bundle_collection
    const currentQuery = queryString.parse(location.search)

    return {
      state,
      bundleCollectionHandle,
      product,
      purchaseType: currentQuery.purchase_type ? currentQuery.purchase_type : PURCHASE_TYPE_SUBSCRIPTION,
      subscriptionFrequency: currentQuery.interval ? currentQuery.interval : null,
      subscriptionDropdown: false,
      quantity: 1,
      activeVariant: null,
      busy: true,
      stickyCartOpen: false,
      stickyAtc: false,
      currentBundle: {},
      bundleId: currentQuery.bundleId ? Number(currentQuery.bundleId) : uniqueNumber()
    }
  },
  created () {
    bus.$on('custom_bundle/ADD_TO_BUNDLE', this.handleAddToBundle)
  },
  mounted () {
    this.stickAtcWatcher = scrollMonitor.create(this.$refs.atcBox)
    this.stickAtcWatcher.enterViewport(() => {
      this.stickyAtc = false
    })

    this.stickAtcWatcher.exitViewport(() => {
      this.stickyAtc = true
    })
  },
  watch: {
    frequencyIntervals () {
      this.subscriptionFrequency = this.frequencyIntervals[0]
    },
    'state.activeVariant': function () {
      this.updateQueryParams()
    },
    purchaseType () {
      this.updateQueryParams()
    },
    subscriptionFrequency () {
      this.updateQueryParams()
    },
    bundleId () {
      this.updateQueryParams()
    },
    'state.bundleProducts': function (newVal, oldVal) {
      if (newVal.length && oldVal.length === 0) {
        this.restoreBundleFromCart()
        this.updateQueryParams()
      }
    }
  },
  computed: {
    formattedCurrentTotal () {
      return `$${formatPrice(this.currentTotal, 2)}`
    },
    currentTotal () {
      return this.state.bundleProducts.reduce((total, product) => {
        if (this.currentBundle[product.id]) {
          if (this.subscriptionSelected && product.subscription['subscription_id']) {
            const price = parseFloat(product.variants[0].subscriptions['discount_variant_price'])
            if (isNaN(price)) return total
            return total + (price * 100 * this.currentBundle[product.id])
          }
          return total + (product.variants[0].price * this.currentBundle[product.id])
        }
        return total
      }, 0)
    },
    subscriptionSelected () {
      return this.purchaseType === PURCHASE_TYPE_SUBSCRIPTION
    },
    hasSubscriptionOption () {
      return true
    },
    firstSubscriptionProduct () {
      return this.state.bundleProducts.find((product) => product.subscription['subscription_id'])
    },
    frequencyIntervals () {
      if (!this.firstSubscriptionProduct) return []
      return this.firstSubscriptionProduct.subscription.shipping_interval_frequency.split(',')
    },
    frequenctIntervalUnit () {
      return this.firstSubscriptionProduct ? this.firstSubscriptionProduct.subscription.shipping_interval_unit_type : ''
    },
    totalQuantity () {
      return this.cartPayload.reduce((total, item) => {
        return total + item.quantity
      }, 0)
    },
    cartPayload () {
      return this.state.bundleProducts.reduce((payload, product) => {
        if (this.currentBundle[product.id]) {
          const variantPayload = {
            variant_id: product.variants[0].id,
            quantity: this.currentBundle[product.id],
            image: product.images[0],
            title: product.title,
            properties: {
              bundle_id: this.bundleId
            }
          }

          if (this.subscriptionSelected && product.subscription['subscription_id']) {
            variantPayload['properties'] = {
              ...variantPayload['properties'],
              handle: product.handle,
              is_subscription: true,
              original_variant_id: product.variants[0].id,
              shipping_interval_frequency: this.subscriptionFrequency,
              shipping_interval_unit_type: this.frequenctIntervalUnit,
              subscription_id: product.subscription.subscription_id,
              discount_percentage: parseFloat(product.subscription.discount_percentage)
            }
            variantPayload['variant_id'] = product.variants[0].subscriptions.discount_variant_id
          }
          payload.push(variantPayload)
        }
        return payload
      }, [])
    }
  },
  methods: {
    frequencySelect (value) {
      this.subscriptionFrequency = value
      this.subscriptionDropdown = !this.subscriptionDropdown
    },
    showFrequencyDropdown () {
      this.subscriptionDropdown = !this.subscriptionDropdown
    },
    async restoreBundleFromCart () {
      this.busy = true
      const { bundleItems = {}, isSubscription = false, interval = null, cartItems = [] } = await this.getBundleDataFromCart()
      if (cartItems.length) {
        Vue.set(this, 'currentBundle', bundleItems)
      } else {
        this.bundleId = uniqueNumber()
      }

      if (isSubscription) {
        this.purchaseType = PURCHASE_TYPE_SUBSCRIPTION
      }

      if (interval !== null) {
        this.subscriptionFrequency = interval
      }

      this.busy = false
    },
    async getBundleDataFromCart () {
      const cart = await Cart.getState()
      const { bundleItems = {}, isSubscription = false, interval = null, cartItems = [] } = cart.items.reduce((currentBundle, item) => {
        if (item.properties && item.properties['bundle_id'] && parseInt(item.properties['bundle_id']) === this.bundleId) {
          if (item.properties['subscription_id']) {
            const matchingProduct = this.state.bundleProducts.find((p) => {
              return Number(p.subscription.discount_product_id) === item.product_id
            })

            if (matchingProduct) {
              currentBundle['bundleItems'][matchingProduct.id] = item.quantity
            }
          } else {
            currentBundle['bundleItems'][item.product_id] = item.quantity
          }
          currentBundle.cartItems.push(item)
          if (item.properties['subscription_id'] && item.properties['shipping_interval_frequency'] && !currentBundle['isSubscription']) {
            currentBundle['isSubscription'] = true
            currentBundle['interval'] = Number(item.properties['shipping_interval_frequency'])
          }
        }
        return currentBundle
      }, {
        bundleItems: {},
        isSubscription: false,
        interval: null,
        cartItems: []
      }) || {}
      return {
        bundleItems,
        isSubscription,
        interval,
        cartItems
      }
    },
    toggleDrawer (target) {
      this.state.activeDrawer = target
    },
    handleAddToBundle ({ product, quantity }) {
      Vue.set(this.currentBundle, product.id, quantity)
    },
    updateQueryParams () {
      setQuery({
        bundleId: this.bundleId,
        purchase_type: this.purchaseType,
        interval: this.subscriptionFrequency
      })
    },
    async addToCart () {
      try {
        this.busy = true
        const bundleFromCart = await this.getBundleDataFromCart()
        if (bundleFromCart.cartItems.length) {
          for (let index = 0; index < bundleFromCart.cartItems.length; index++) {
            const cartItem = bundleFromCart.cartItems[index]
            await Cart.remove(cartItem.key)
          }
        }
        for (let index = 0; index < this.cartPayload.length; index++) {

        }
        const payload = this.cartPayload.map((lineItem) => {
          return {
            id: lineItem.variant_id,
            quantity: lineItem.quantity,
            properties: lineItem.properties
          }
        })
        await Cart.add(payload, true)
        // window.location = '/cart'
        window.MicroBus.emit(`side-cart:toggle`)
      } catch (error) {
        console.log(error)
      } finally {
        this.busy = false
      }
    }
  },
  beforeDestroy () {
    bus.$off('custom_bundle/ADD_TO_BUNDLE')
  }
})
